import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import octopusAnimation from "../assets/Octopus.json";
import image1 from "../assets/1.jpg";
import image2 from "../assets/2.jpg";
import image3 from "../assets/3.jpg";
import image4 from "../assets/4.jpg";
import image5 from "../assets/5.jpg";
import image6 from "../assets/6.jpg";
import image7 from "../assets/7.jpg";
import image8 from "../assets/8.jpg";
import image9 from "../assets/9.jpg";
import image10 from "../assets/10.jpg";
import image11 from "../assets/11.jpg";
import scholarshipImg from "../assets/scholarship.gif";
import whatsappIcon from "../assets/whatsapp.png";

const ThinkyPlayLanding = () => {
  const navigate = useNavigate();
  const [showPopup, setShowPopup] = useState(false);
  const [joinMessage, setJoinMessage] = useState("");
  const intervalRef = useRef(null);
  const isMessageDisplayed = useRef(false);

  // List of 30 Indian names
  const indianNames = [
    "Aarav", "Aditi", "Amit", "Ananya", "Anil", "Arjun", "Bhavna", "Deepak",
    "Divya", "Gaurav", "Isha", "Karan", "Kavita", "Manish", "Meera", "Mohan",
    "Neha", "Nikhil", "Pooja", "Pranav", "Priya", "Rahul", "Riya", "Rohan",
    "Sanjay", "Shalini", "Shiv", "Sneha", "Vikas", "Vishal"
  ];

  // List of 20 non-Indian names
  const nonIndianNames = [
    "James", "Emma", "Liam", "Olivia", "Noah", "Sophia", "William", "Isabella",
    "Lucas", "Mia", "Henry", "Charlotte", "Alexander", "Amelia", "Michael",
    "Harper", "Daniel", "Evelyn", "Jacob", "Ava"
  ];

  // List of 30 Indian titles
  const indianTitles = [
    "Sharma", "Verma", "Gupta", "Singh", "Kumar", "Patel", "Mehta", "Joshi",
    "Pandey", "Mishra", "Yadav", "Rao", "Nair", "Iyer", "Chopra", "Dubey",
    "Bhat", "Menon", "Srivastava", "Reddy", "Malhotra", "Bose", "Das", "Jain",
    "Agarwal", "Kapoor", "Pillai", "Thakur", "Borah", "Chatterjee"
  ];

  // List of 20 non-Indian titles
  const nonIndianTitles = [
    "Smith", "Johnson", "Brown", "Taylor", "Wilson", "Davis", "Clark", "Harris",
    "Lewis", "Walker", "Hall", "Allen", "Young", "King", "Wright", "Scott",
    "Green", "Adams", "Baker", "Nelson"
  ];

  // Shuffle function
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Combine names and titles
  const createStudents = () => {
    const shuffledIndianNames = shuffleArray(indianNames);
    const shuffledIndianTitles = shuffleArray(indianTitles);
    const shuffledNonIndianNames = shuffleArray(nonIndianNames);
    const shuffledNonIndianTitles = shuffleArray(nonIndianTitles);

    const indianStudents = shuffledIndianNames.map((name, index) => ({
      name,
      title: shuffledIndianTitles[index],
      isIndian: true,
    }));

    const nonIndianStudents = shuffledNonIndianNames.map((name, index) => ({
      name,
      title: shuffledNonIndianTitles[index],
      isIndian: false,
    }));

    return [...indianStudents, ...nonIndianStudents];
  };

  const students = createStudents();

  useEffect(() => {
    const showRandomJoinMessage = () => {
      const randomIndex = Math.floor(Math.random() * students.length);
      const student = students[randomIndex];
      setJoinMessage(`${student.name} ${student.title} just joined us! Welcome🖐️`);
      isMessageDisplayed.current = true;
      setTimeout(() => {
        setJoinMessage("");
        isMessageDisplayed.current = false;
      }, 10000); // Message displays for 10 seconds
    };

    const messageInterval = setInterval(() => {
      if (!isMessageDisplayed.current) {
        showRandomJoinMessage();
      }
    }, 37000); // New message every 37 seconds

    const popupInterval = setInterval(() => {
      setShowPopup(true);
    }, 40000);

    return () => {
      clearInterval(messageInterval);
      clearInterval(popupInterval);
    };
  }, [students]);

  const handleCTAClick = useCallback(() => navigate("/account"), [navigate]);

  const handleCloseJoinMessage = () => {
    setJoinMessage("");
    isMessageDisplayed.current = false;
  };

  return (
    <div className="page-wrapper">
      <style>{`
        :root {
          --primary: #1E90FF;
          --dark: #1a1a1a;
          --light: #ffffff;
          --neutral: #4a5568;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Inter', sans-serif;
          background: var(--light);
          color: var(--dark);
          line-height: 1.6;
          -webkit-font-smoothing: antialiased;
          overflow-x: hidden;
        }

        .page-wrapper {
          width: 100%;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
        }

        .header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 20;
          background: var(--light);
          padding: 1rem;
          border-bottom: 1px solid rgba(30, 144, 255, 0.2);
        }

        .header-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
          max-width: 1280px;
          margin: 0 auto;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .logo-container {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .logo-container span {
          font-size: clamp(1.25rem, 5vw, 1.5rem);
          font-weight: 800;
          letter-spacing: -0.02em;
          color: var(--dark);
          text-align: center;
        }

        .main-content {
          padding-top: 90px;
          display: flex;
          flex-direction: column;
          width: 100%;
          align-items: center;
        }

        .image-container {
          width: 100%;
          display: flex;
          flex-direction: column;
        }

        .responsive-image {
          width: 100%;
          height: auto;
          display: block;
          aspect-ratio: 1920 / 1080;
          object-fit: cover;
        }

        .button {
          display: inline-flex;
          justify-content: center;
          align-items: center;
          padding: 0.75rem 2rem;
          border-radius: 2rem;
          border: 2px solid var(--primary);
          background: var(--primary);
          color: var(--light);
          font-weight: 600;
          font-size: clamp(0.875rem, 4vw, 1rem);
          text-decoration: none;
          cursor: pointer;
          width: 100%;
          max-width: 300px;
          margin: 2rem auto;
          transition: all 0.3s ease;
        }

        .button:hover {
          background: transparent;
          color: var(--primary);
        }

        .login-button {
          max-width: 100px;
          margin: 0 auto;
          background: transparent;
          color: var(--primary);
        }

        .login-button:hover {
          background: var(--primary);
          color: var(--light);
        }

        .join-message-card {
          background: var(--light);
          border: 1px solid rgba(30, 144, 255, 0.2);
          border-radius: 0.5rem;
          padding: 0.75rem 1.25rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          max-width: 300px;
          width: 100%;
          z-index: 10;
          opacity: 0;
          animation: fadeInOut 10s ease-in-out;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          margin: 0 auto;
        }

        .join-message {
          font-size: clamp(0.875rem, 3.5vw, 1rem);
          color: var(--neutral);
          text-align: center;
          margin: 0;
          flex: 1;
        }

        .join-message-close-btn {
          background: none;
          border: none;
          color: var(--neutral);
          font-size: 1rem;
          cursor: pointer;
          transition: color 0.3s ease;
        }

        .join-message-close-btn:hover {
          color: var(--primary);
        }

        @keyframes fadeInOut {
          0% { opacity: 0; transform: translateY(10px); }
          10% { opacity: 1; transform: translateY(0); }
          90% { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(10px); }
        }

        .exit-popup {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.75);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 100;
          padding: 1rem;
        }

        .popup-content {
          position: relative;
          width: 100%;
          max-width: 400px;
          background: var(--light);
          border: 1px solid rgba(30, 144, 255, 0.2);
          border-radius: 0.75rem;
          padding: 1.5rem;
          text-align: center;
          box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        }

        .close-btn {
          position: absolute;
          top: 0.5rem;
          right: 0.5rem;
          background: none;
          border: none;
          color: var(--neutral);
          font-size: 1.25rem;
          cursor: pointer;
          transition: color 0.3s ease;
        }

        .close-btn:hover {
          color: var(--primary);
        }

        .popup-image {
          max-width: 100%;
          margin: 1rem auto;
          display: block;
          border-radius: 0.75rem;
        }

        .ask-us-section {
          width: 100%;
          padding: 2rem 1rem;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.8rem;
          background: var(--light);
          border-top: 1px solid rgba(30, 144, 255, 0.2);
        }

        .ask-us-link {
          font-size: clamp(1.3rem, 5vw, 2rem);
          font-weight: 700;
          color: var(--dark);
          text-decoration: none;
          transition: color 0.3s ease;
        }

        .ask-us-link:hover {
          color: var(--primary);
        }

        .whatsapp-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 60px;
          height: 60px;
          border-radius: 50%;
          transition: all 0.3s ease;
        }

        .whatsapp-icon img {
          width: 32px;
          height: 32px;
        }

        .whatsapp-icon:hover {
          opacity: 0.8;
        }

        @media (min-width: 640px) {
          .header { padding: 1.25rem 2rem; }
          .main-content { padding-top: 100px; }
          .button { max-width: 350px; }
          .join-message-card { max-width: 350px; }
          .ask-us-section { padding: 2rem; }
        }

        @media (min-width: 1024px) {
          .header { padding: 1.5rem 3rem; }
          .main-content { padding-top: 120px; }
          .button { max-width: 400px; }
          .join-message-card { max-width: 400px; }
          .ask-us-section { padding: 3rem; }
          .whatsapp-icon { width: 80px; height: 80px; }
          .whatsapp-icon img { width: 40px; height: 40px; }
        }
      `}</style>

      <head>
        <title>100% Safe Internet for Students!</title>
        <meta name="description" content="Welcome to the internet designed for the next generation of kids." />
      </head>

      <header className="header">
        <div className="header-content">
          <div className="logo-container">
            <Lottie animationData={octopusAnimation} loop={true} style={{ width: 60, height: 60 }} />
            <span>drOcto</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <button className="button login-button" onClick={handleCTAClick}>
              <span>Login</span>
            </button>
          </div>
        </div>
      </header>

      <main className="main-content">
        <div className="image-container">
          {[image1, image2, image3, image4, image5, image6, image7, image8, image9, image10, image11].map((image, index) => (
            <img
              key={index}
              src={image}
              alt={`Image ${index + 1}`}
              className="responsive-image"
            />
          ))}
        </div>
        <button className="button" onClick={handleCTAClick}>Start Now</button>
        {joinMessage && (
          <div className="join-message-card">
            <p className="join-message">{joinMessage}</p>
            <button className="join-message-close-btn" onClick={handleCloseJoinMessage}>
              ✕
            </button>
          </div>
        )}
        <section className="ask-us-section">
          <a href="https://wa.me/+918472958581" target="_blank" rel="noopener noreferrer" className="ask-us-link">
            Click to Ask Anything
          </a>
          <a href="https://wa.me/+918472958581" target="_blank" rel="noopener noreferrer" className="whatsapp-icon">
            <img src={whatsappIcon} alt="WhatsApp" />
          </a>
        </section>
      </main>

      {showPopup && (
        <div className="exit-popup">
          <div className="popup-content">
            <button className="close-btn" onClick={() => setShowPopup(false)}>
              ✕
            </button>
            <h2>Claim Your Scholarship Offer!</h2>
            <img
              src={scholarshipImg}
              alt="Scholarship Offer"
              className="popup-image"
              style={{ maxWidth: "100%", margin: "1rem auto", display: "block" }}
            />
            <p>Don’t miss out on this exclusive offer for your child’s future!</p>
            <button className="button" onClick={handleCTAClick}>Claim Offer</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ThinkyPlayLanding;